// Copyright (c) StrangeBee 2023

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { useLocalMessage } from '@Helpers/locale';
import { urlValidator } from '@Helpers/string';
import useValidateForm from '@Hooks/useValidateForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { OrgSelector } from '@Organisms/FormConfig/OrgSelector/OrgSelector';
import React, { ReactElement } from 'react';
import { AuthType, httpMethods, WebhookOutput, WebhookType, webhookTypeAvailable, WsConfigInput } from 'thehive-sdk';
import { AuthFormItems } from '../Macros/AuthFormItems';
import { THListChoice } from '@Molecules/Inputs/ThListChoice/THListChoice';
import { endpointLogos } from '@Pages/Admin/Organisations/Details/Endpoints/List';
import { HeadersFormItems } from '../Macros/HeadersFormItems';
import { Form } from 'antd';
import WsConfigForm from '@Organisms/FormConfig/WsConfigForm/WsConfigForm';
import { computeWsConfig } from '@Helpers/wsConfig';

import './EndpointManage.scss';

interface WebhookListChoice {
    name: WebhookType;
    disabled?: boolean;
}

export interface EndpointFormValues {
    type?: {
        name: WebhookType;
        disabled: boolean;
    };
    name: string;
    url: string;
    version?: number;
    auth: AuthType;
    hasWsConfig?: boolean;
    wsConfig?: WsConfigInput;
    filterOrganisation: 'all' | 'include' | 'exclude';
    includedTheHiveOrganisations?: string[];
    excludedTheHiveOrganisations?: string[];
    headers?: {
        key: string;
        value: string;
    }[];
    method?: string;
}

interface EndpointManageProps {
    onFinish: (values: EndpointFormValues) => Promise<any>;
    triggerElem?: ReactElement;
    canEditOrganisation?: boolean;
    defaultValues?: Partial<WebhookOutput>;
    endpointNames?: string[];
    notificationCapability?: string[];
    disableTypeChoose?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    visible?: boolean;
}

export const EndpointManage = ({
    triggerElem,
    onFinish,
    canEditOrganisation,
    defaultValues,
    endpointNames,
    notificationCapability,
    disableTypeChoose = false,
    onVisibleChange,
    visible,
}: EndpointManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinishHandler = ({ headers, ...values }: EndpointFormValues): Promise<any> => {
        const wsConfig = values.wsConfig && computeWsConfig(values.wsConfig);

        return onFinish({
            ...values,
            headers: headers?.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}) as any,
            wsConfig,
        });
    };

    const type = Form.useWatch(['type'], form);
    const filterOrganisation = Form.useWatch(['filterOrganisation'], form);

    return (
        <THDrawer
            title={!!defaultValues ? i('endpoint.form.title.edit') : i('endpoint.form.title.create')}
            triggerElem={triggerElem}
            className="drawer-form"
            destroyOnClose
            afterVisibleChange={(visible) => {
                form.resetFields();
                onFormValuesChange();
                onVisibleChange?.(visible);
            }}
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <THForm<EndpointFormValues>
                    name="endpoint-form"
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    initialValues={{
                        auth: { type: 'none' },
                        includedTheHiveOrganisations: [],
                        excludedTheHiveOrganisations: [],
                        ...defaultValues,
                        type: defaultValues?.type ? { name: defaultValues?.type, disabled: false } : undefined,
                        headers:
                            defaultValues?.type === 'http'
                                ? Object.entries((defaultValues as any)?.headers || {})?.map(([key, value]) => ({
                                      key,
                                      value,
                                  }))
                                : [],
                        version: defaultValues?.version === undefined ? 1 : defaultValues?.version,
                        wsConfig: {
                            ...defaultValues?.wsConfig,
                            ssl: {
                                ...defaultValues?.wsConfig?.ssl,
                                loose: {
                                    checkCertificateAuthority: !defaultValues?.wsConfig?.ssl.loose.acceptAnyCertificate,
                                },
                            },
                        },
                    }}
                    onFinish={(values) => {
                        onFinishHandler(values).then(() => {
                            toggleDrawer();
                            setFieldsTouched(false);
                        });
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    setFieldsTouched(false);
                                    toggleDrawer();
                                    form.resetFields();
                                }}
                                formName="endpoint-form"
                                disabled={!isValid || !fieldsTouched}
                            />
                        }
                    >
                        <THForm.Item
                            name="type"
                            label={i('endpoint.form.type.label')}
                            rules={[{ required: true, message: i('endpoint.form.type.required') }]}
                        >
                            <THListChoice<WebhookListChoice>
                                dataSource={webhookTypeAvailable.map((value) => ({
                                    name: value,
                                    disabled:
                                        !notificationCapability?.includes(value) ||
                                        (disableTypeChoose && defaultValues && defaultValues?.type !== value),
                                }))}
                                disabled={!!defaultValues?.type && !defaultValues?.name}
                            >
                                {(item) => (
                                    <div>
                                        <span className="endpoint-manage__logo">{endpointLogos[item.name]}</span>
                                        {item.name
                                            .split('')
                                            .map((char, index) => (index === 0 ? char.toUpperCase() : char))
                                            .join('')}
                                    </div>
                                )}
                            </THListChoice>
                        </THForm.Item>

                        <THForm.Item
                            name="name"
                            label={i('endpoint.form.name.label')}
                            hidden={!type?.name}
                            rules={[
                                {
                                    required: true,
                                    message: i('endpoint.form.name.required'),
                                    validator: (_, value) =>
                                        endpointNames?.includes(value) || !value
                                            ? Promise.reject(i('endpoint.form.name.required'))
                                            : Promise.resolve(),
                                },
                            ]}
                        >
                            <THInput
                                placeholder={i('endpoint.form.name.placeholder')}
                                disabled={!!defaultValues?.name}
                            />
                        </THForm.Item>

                        <THForm.Item
                            name="method"
                            label={i('endpoint.form.connector.http.method.label')}
                            hidden={type?.name !== 'http'}
                            rules={[{ required: type?.name === 'http' }]}
                        >
                            <THSelect
                                options={httpMethods.map((method) => ({ label: method, value: method }))}
                                placeholder={i('endpoint.form.connector.http.method.placeholder')}
                            />
                        </THForm.Item>

                        <THForm.Item
                            name="url"
                            label="Url"
                            rules={[
                                {
                                    message: i('notification.notifiers.http-request.url.required'),
                                    required: ['webhook', 'mattermost', 'http', 'teams'].includes(type?.name),
                                    validator: ['webhook', 'mattermost', 'http', 'teams'].includes(type?.name)
                                        ? urlValidator
                                        : undefined,
                                },
                            ]}
                            hidden={!['webhook', 'mattermost', 'http', 'teams'].includes(type?.name)}
                        >
                            <THInput placeholder={i('notification.notifiers.http-request.url.placeholder')} />
                        </THForm.Item>

                        <THForm.Item
                            name="token"
                            label="Token"
                            hidden={type?.name !== 'slack'}
                            rules={[
                                {
                                    required: type?.name === 'slack',
                                    message: i('endpoint.form.connector.slack.token.required'),
                                },
                            ]}
                        >
                            <THInput placeholder={i('endpoint.form.connector.slack.token.placeholder')} />
                        </THForm.Item>

                        <THForm.Item name="version" label="Version" hidden={type?.name !== 'webhook'}>
                            <THInputNumber placeholder="Enter a version" />
                        </THForm.Item>

                        <THForm.Item
                            name="username"
                            label={i('endpoint.form.connector.mattermost.username.label')}
                            hidden={type?.name !== 'mattermost'}
                        >
                            <THInput placeholder={i('endpoint.form.connector.mattermost.username.placeholder')} />
                        </THForm.Item>

                        <THForm.Item
                            name="channel"
                            label={i('endpoint.form.connector.mattermost.channel.label')}
                            hidden={type?.name !== 'mattermost'}
                        >
                            <THInput placeholder={i('endpoint.form.connector.mattermost.channel.placeholder')} />
                        </THForm.Item>

                        {type?.name === 'http' && <HeadersFormItems />}

                        {canEditOrganisation && (
                            <>
                                <TitleSection className="endpoint-manage__title-section">Organisations</TitleSection>

                                <THForm.Item
                                    name={'filterOrganisation'}
                                    label={i('configPlateform.field.filterOrganization')}
                                >
                                    <THSelect
                                        placeholder={i('configPlateform.placeholder.filter-organisation')}
                                        options={[
                                            {
                                                value: 'all',
                                                label: i('configPlateform.options.all'),
                                            },
                                            {
                                                value: 'include',
                                                label: i('configPlateform.options.include'),
                                            },
                                            {
                                                value: 'exclude',
                                                label: i('configPlateform.options.exlude'),
                                            },
                                        ]}
                                    />
                                </THForm.Item>

                                <THForm.Item
                                    name="includedTheHiveOrganisations"
                                    label="Organisations to include"
                                    hidden={filterOrganisation !== 'include'}
                                >
                                    <OrgSelector />
                                </THForm.Item>

                                <THForm.Item
                                    name="excludedTheHiveOrganisations"
                                    label="Organisations to exclude"
                                    hidden={filterOrganisation !== 'exclude'}
                                >
                                    <OrgSelector />
                                </THForm.Item>
                            </>
                        )}
                        {!!type?.name && (
                            <>
                                {type.name !== 'teams' && <AuthFormItems local={i} endpointTypeName={type.name} />}

                                <WsConfigForm name={['wsConfig']} form={form} />
                            </>
                        )}
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
